import {AfterViewInit, Component, OnDestroy, ViewEncapsulation} from '@angular/core';
import {AgFilterComponent} from 'ag-grid-angular';
import {IDoesFilterPassParams, IFilterParams} from 'ag-grid-community';
import {CommonModule} from '@angular/common';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatFormField} from '@angular/material/form-field';
import {MatOption, MatSelect} from '@angular/material/select';
import {
  DateElementComponent
} from '../../../form-control/dynamic-form/form-element/date-element/date-element.component';
import {AgFilterEventPreventionMixin} from '../ag-filter-event-prevention.mixin';

interface ICustomFilterParams extends IFilterParams {
  valueFormatter?: (value: any) => string;
}

type TDateFilterType =
  | 'equals'
  | 'notEqual'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'inRange'
  | 'blank'
  | 'notBlank';

@Component({
  selector: 'app-date-filter',
  templateUrl: './date-filter.component.html',
  styleUrls: ['./date-filter.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatSelect,
    MatOption,
    DateElementComponent,
  ],
})
export class DateFilterComponent extends AgFilterEventPreventionMixin implements AgFilterComponent, AfterViewInit, OnDestroy {
  params: ICustomFilterParams;

  // First condition
  filterType: TDateFilterType = 'equals';
  dateFromControl: FormControl = new FormControl('');
  dateToControl: FormControl = new FormControl('');

  // Second condition
  filterType2: TDateFilterType = 'equals';
  dateFrom2Control: FormControl = new FormControl('');
  dateTo2Control: FormControl = new FormControl('');

  joinOperator: 'AND' | 'OR' = 'AND';

  private clickedButton: HTMLButtonElement | null = null;

  filterOptions: { value: TDateFilterType; label: string }[] = [
    {value: 'equals', label: 'Gleich'},
    {value: 'greaterThan', label: 'Größer als'},
    {value: 'lessThan', label: 'Kleiner als'},
    {value: 'notEqual', label: 'Ungleich'},
    {value: 'inRange', label: 'Im Intervall'},
    {value: 'blank', label: 'Leer'},
    {value: 'notBlank', label: 'Nicht leer'},
  ];

  constructor() {
    super();
  }

  agInit(params: ICustomFilterParams): void {
    this.params = params;
  }

  ngAfterViewInit(): void {
    this.addDocumentClickListener();
  }

  ngOnDestroy(): void {
    this.removeDocumentClickListener();
    this.removeOperatorSelectDocumentClickListener();
  }

  isFilterActive(): boolean {
    if (this.filterType === 'blank' || this.filterType === 'notBlank') {
      return true;
    }

    if (this.filterType === 'inRange') {
      return !!this.dateFromControl.value && !!this.dateToControl.value;
    }

    return !!this.dateFromControl.value;
  }

  doesFilterPass(params: IDoesFilterPassParams): boolean {
    return true;
  }

  getModel(): any {
    if (!this.isFilterActive()) {
      return null;
    }

    const model: any = {
      filterType: 'date',
      type: this.filterType,
    };

    if (this.filterType !== 'blank' && this.filterType !== 'notBlank') {
      model.dateFrom = this.dateFromControl.value;

      if (this.filterType === 'inRange') {
        model.dateTo = this.dateToControl.value;
      }
    }

    // Add a second condition if active
    if (this.isSecondConditionActive()) {
      model.operator = this.joinOperator;
      model.condition1 = {
        filterType: 'date',
        type: this.filterType,
      };

      if (this.filterType !== 'blank' && this.filterType !== 'notBlank') {
        model.condition1.dateFrom = this.dateFromControl.value;
        if (this.filterType === 'inRange') {
          model.condition1.dateTo = this.dateToControl.value;
        }
      }

      model.condition2 = {
        filterType: 'date',
        type: this.filterType2,
      };

      if (this.filterType2 !== 'blank' && this.filterType2 !== 'notBlank') {
        model.condition2.dateFrom = this.dateFrom2Control.value;
        if (this.filterType2 === 'inRange') {
          model.condition2.dateTo = this.dateTo2Control.value;
        }
      }
    }

    return model;
  }

  setModel(model: any): void {
    if (model) {
      // Check if this is a combined filter model (with condition1 and condition2)
      if (model.condition1 && model.condition2) {
        // Restore the first condition
        this.filterType = model.condition1.type || 'equals';
        this.dateFromControl.setValue(model.condition1.dateFrom || '', {emitEvent: false});
        this.dateToControl.setValue(model.condition1.dateTo || '', {emitEvent: false});

        // Restore the second condition
        this.filterType2 = model.condition2.type || 'equals';
        this.dateFrom2Control.setValue(model.condition2.dateFrom || '', {emitEvent: false});
        this.dateTo2Control.setValue(model.condition2.dateTo || '', {emitEvent: false});

        // Restore the join operator
        this.joinOperator = model.operator || 'AND';
      } else {
        this.filterType = model.type || 'equals';
        this.dateFromControl.setValue(model.dateFrom || '', {emitEvent: false});
        this.dateToControl.setValue(model.dateTo || '', {emitEvent: false});
      }
    } else {
      this.clearFilter();
    }
  }

  onFilterChanged(): void {
    this.params.filterChangedCallback();
  }

  applyFilter(): void {
    this.onFilterChanged();
  }

  clearFilter(): void {
    this.dateFromControl.setValue('', {emitEvent: false});
    this.dateToControl.setValue('', {emitEvent: false});
    this.filterType = 'equals';
    this.dateFrom2Control.setValue('', {emitEvent: false});
    this.dateTo2Control.setValue('', {emitEvent: false});
    this.filterType2 = 'equals';
    this.joinOperator = 'AND';
    this.onFilterChanged();
  }

  onFilterTypeChange(): void {
    return; // Don't trigger filter automatically - let the user click "Apply"
  }

  showDateTo(): boolean {
    return this.filterType === 'inRange';
  }

  showDateInput(): boolean {
    return this.filterType !== 'blank' && this.filterType !== 'notBlank';
  }

  showDateTo2(): boolean {
    return this.filterType2 === 'inRange';
  }

  showDateInput2(): boolean {
    return this.filterType2 !== 'blank' && this.filterType2 !== 'notBlank';
  }

  showSecondCondition(): boolean {
    return this.isFirstConditionComplete();
  }

  private isFirstConditionComplete(): boolean {
    if (this.filterType === 'blank' || this.filterType === 'notBlank') {
      return true;
    }
    if (this.filterType === 'inRange') {
      return !!this.dateFromControl.value && !!this.dateToControl.value;
    }
    return !!this.dateFromControl.value;
  }

  private isSecondConditionActive(): boolean {
    if (this.filterType2 === 'blank' || this.filterType2 === 'notBlank') {
      return true;
    }
    if (this.filterType2 === 'inRange') {
      return !!this.dateFrom2Control.value && !!this.dateTo2Control.value;
    }
    return !!this.dateFrom2Control.value;
  }


  private addDocumentClickListener(): void {
    this.removeDocumentClickListener();

    this.documentClickListener = (event: MouseEvent): void => {
      const target: HTMLElement = event.target as HTMLElement;

      // Check if clicking on a button
      const buttonElement: HTMLButtonElement = target.closest('.ag-filter-apply-panel-button') as HTMLButtonElement;
      if (buttonElement) {
        this.clickedButton = buttonElement;
        return;
      }

      // Check if in the date picker overlay, date input, or filter wrapper
      const isInDatepickerOverlay: boolean = target.closest('ngb-datepicker') !== null;
      const isInDateInput: boolean = target.closest('.date-input-wrapper') !== null;
      const isInFilterWrapper: boolean = target.closest('.ag-filter-wrapper') !== null;

      if (isInDatepickerOverlay || isInDateInput || isInFilterWrapper) {
        event.stopPropagation();
      }
    };

    this.documentKeyboardListener = (event: KeyboardEvent): void => {
      const target: HTMLElement = event.target as HTMLElement;

      // Check if in the date picker overlay, date input, or filter wrapper
      const isInDatepickerOverlay: boolean = target.closest('ngb-datepicker') !== null;
      const isInDateInput: boolean = target.closest('.date-input-wrapper') !== null;
      const isInFilterWrapper: boolean = target.closest('.ag-filter-wrapper') !== null;

      if (isInDatepickerOverlay || isInDateInput || isInFilterWrapper) {
        event.stopPropagation();
      }
    };

    document.addEventListener('mousedown', this.documentClickListener, {capture: true});
    document.addEventListener('keydown', this.documentKeyboardListener, {capture: false});
    document.addEventListener('keyup', this.documentKeyboardListener, {capture: false});
  }
}
